/**
 * One right-panel surface tab: its context menu, close affordance, rename in
 * place, and mute indicator.
 *
 * Split out of `rightSidebarSurfaceTabStrip` so the strip stays a list of
 * tabs plus the plus menu while the single tab owns its own interaction.
 * Rename and mute port t3code's RightPanelTabs capabilities: rename lets the
 * user name a tab in place, and mute addresses the desktop browser view
 * behind a preview tab, offered only where that bridge exists.
 */
import { VolumeOffIcon, XIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { cn } from "~/lib/utils";

import {
  buildRightPanelTabMenu,
  resolveRightPanelTabCloseTargets,
  type RightPanelTabAction,
  type RightPanelTabMenuItem,
} from "./rightPanelTabContextMenu.logic";
import { RIGHT_SIDEBAR_TAB_ICON as TAB_ICON } from "./RightSidebarShell.icons";
import type { RightPanelSurfaceTab } from "./rightPanelSurfaceTabs";
import type { RightPanelPreviewAudio } from "./rightSidebarSurfaceTabStrip";
import { surfaceTabSurfaceClass } from "./rightSidebarSurfaceTab";
import { Menu, MenuItem, MenuPopup } from "./ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

interface SurfaceTabProps {
  readonly tab: RightPanelSurfaceTab;
  /** The whole strip, because the bulk-close actions are relative to it. */
  readonly tabs: ReadonlyArray<RightPanelSurfaceTab>;
  readonly onSelect: (surfaceId: string) => void;
  readonly onClose: (surfaceId: string) => void;
  readonly onRenameSurface?: ((surfaceId: string, label: string) => void) | undefined;
  readonly previewAudio?: RightPanelPreviewAudio | undefined;
}

/**
 * What the tab's menu may offer, derived from the tab and what the shell
 * could actually wire up. A capability without its handler stays unoffered:
 * an inert menu item is worse than an absent one.
 */
function surfaceTabCapabilities(input: {
  readonly tab: RightPanelSurfaceTab;
  readonly hasRenameHandler: boolean;
  readonly hasPreviewAudio: boolean;
}): { readonly canRename: boolean; readonly canToggleMute: boolean } {
  return {
    canRename: input.tab.renamable === true && input.hasRenameHandler,
    canToggleMute: input.hasPreviewAudio && input.tab.kind === "preview",
  };
}

/**
 * One context-menu action, dispatched outside the component so the tab itself
 * stays a renderer. Close targets are resolved once, before anything closes:
 * recomputing between removals would re-index the strip underneath a
 * partially-applied "close to the right" and take the wrong tabs.
 */
function runSurfaceTabMenuAction(input: {
  readonly action: RightPanelTabAction;
  readonly surfaceId: string;
  readonly tabs: ReadonlyArray<RightPanelSurfaceTab>;
  readonly onClose: (surfaceId: string) => void;
  readonly onStartRename: () => void;
  readonly onToggleMute: ((surfaceId: string) => void) | undefined;
  readonly onDismiss: () => void;
}): void {
  if (input.action === "rename") {
    input.onStartRename();
  } else if (input.action === "toggle-mute") {
    input.onToggleMute?.(input.surfaceId);
  } else {
    for (const surfaceId of resolveRightPanelTabCloseTargets({
      action: input.action,
      tabs: input.tabs,
      surfaceId: input.surfaceId,
    })) {
      input.onClose(surfaceId);
    }
  }
  input.onDismiss();
}

/**
 * One tab: icon, label, mute indicator, rename in place, and the context
 * menu. The tab only renders; menu semantics live in
 * `runSurfaceTabMenuAction`.
 */
export function SurfaceTab(props: SurfaceTabProps) {
  const { tab, tabs, onSelect, onClose, onRenameSurface, previewAudio } = props;
  const Icon = TAB_ICON[tab.kind];
  const [menuPoint, setMenuPoint] = useState<{ x: number; y: number } | null>(null);
  const [renaming, setRenaming] = useState(false);

  const muted = previewAudio?.mutedSurfaceIds.has(tab.id) ?? false;
  const { canRename, canToggleMute } = surfaceTabCapabilities({
    tab,
    hasRenameHandler: onRenameSurface !== undefined,
    hasPreviewAudio: previewAudio !== undefined,
  });

  const menuItems = useMemo(
    () =>
      buildRightPanelTabMenu({
        tabs,
        surfaceId: tab.id,
        renamable: canRename,
        canToggleMute,
        muted,
      }),
    [canRename, canToggleMute, muted, tab.id, tabs],
  );

  const runMenuAction = useCallback(
    (action: RightPanelTabAction) => {
      runSurfaceTabMenuAction({
        action,
        surfaceId: tab.id,
        tabs,
        onClose,
        onStartRename: () => setRenaming(true),
        onToggleMute: previewAudio?.onToggleMute,
        onDismiss: () => setMenuPoint(null),
      });
    },
    [onClose, previewAudio, tab.id, tabs],
  );

  return (
    <div
      role="tab"
      onContextMenu={(event) => {
        event.preventDefault();
        setMenuPoint({ x: event.clientX, y: event.clientY });
      }}
      aria-selected={tab.active ? "true" : "false"}
      aria-label={tab.label}
      data-testid={`right-sidebar-surface-tab-${tab.kind}`}
      data-surface-id={tab.id}
      data-active={tab.active ? "true" : "false"}
      className={cn(
        "group/surface-tab inline-flex min-h-11 max-w-36 shrink-0 items-center gap-0.5 rounded-xl px-3 text-sm font-medium transition-colors focus-within:ring-1 focus-within:ring-ring sm:h-6 sm:min-h-0 sm:rounded-md sm:pr-2 sm:pl-1.5 sm:text-xs",
        surfaceTabSurfaceClass({ active: tab.active, disabled: tab.disabled ?? false }),
      )}
    >
      <SurfaceTabMenu
        tab={tab}
        menuItems={menuItems}
        menuPoint={menuPoint}
        onMenuPointChange={setMenuPoint}
        onAction={runMenuAction}
      />
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label={`Close ${tab.label} surface`}
              data-testid={`right-sidebar-surface-tab-close-${tab.kind}`}
              onClick={() => onClose(tab.id)}
              className="inline-flex size-8 items-center justify-center rounded-full text-current transition-colors hover:bg-background/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:size-4"
            >
              <Icon
                aria-hidden="true"
                className="size-3 transition-opacity group-hover/surface-tab:hidden group-focus-within/surface-tab:hidden"
              />
              <XIcon
                aria-hidden="true"
                className="hidden size-3 group-hover/surface-tab:block group-focus-within/surface-tab:block"
              />
            </button>
          }
        />
        <TooltipPopup side="bottom">Close {tab.label}</TooltipPopup>
      </Tooltip>
      {muted ? <SurfaceTabMuteButton tab={tab} onToggleMute={previewAudio?.onToggleMute} /> : null}
      <SurfaceTabLabel
        tab={tab}
        renaming={renaming}
        onSelect={onSelect}
        onStartRename={() => {
          if (canRename) setRenaming(true);
        }}
        onCommitRename={(value) => {
          onRenameSurface?.(tab.id, value);
          setRenaming(false);
        }}
      />
      {tab.statusLabel ? (
        <span className="hidden rounded-full border border-border/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline">
          {tab.statusLabel}
        </span>
      ) : null}
    </div>
  );
}

interface SurfaceTabMenuProps {
  readonly tab: RightPanelSurfaceTab;
  readonly menuItems: ReadonlyArray<RightPanelTabMenuItem>;
  readonly menuPoint: { x: number; y: number } | null;
  readonly onMenuPointChange: (point: { x: number; y: number } | null) => void;
  readonly onAction: (action: RightPanelTabAction) => void;
}

/**
 * The tab's context menu, positioned by a virtual anchor at the cursor point
 * the tab recorded on right-click.
 */
function SurfaceTabMenu(props: SurfaceTabMenuProps) {
  return (
    <Menu
      open={props.menuPoint !== null}
      onOpenChange={(open) => {
        if (!open) props.onMenuPointChange(null);
      }}
    >
      <MenuPopup
        // A virtual anchor at the cursor. Base UI positions against any
        // object with getBoundingClientRect, which is how a context menu
        // opens where the pointer is rather than against the tab.
        anchor={
          props.menuPoint
            ? {
                getBoundingClientRect: () =>
                  new DOMRect(props.menuPoint!.x, props.menuPoint!.y, 0, 0),
              }
            : undefined
        }
        side="bottom"
        align="start"
        aria-label={`${props.tab.label} tab actions`}
        data-testid={`right-sidebar-surface-tab-menu-${props.tab.kind}`}
      >
        {props.menuItems.map((item) => (
          <MenuItem
            key={item.action}
            disabled={item.disabled}
            data-testid={`right-sidebar-surface-tab-menu-item-${item.action}`}
            onClick={() => props.onAction(item.action)}
          >
            {item.label}
          </MenuItem>
        ))}
      </MenuPopup>
    </Menu>
  );
}

interface SurfaceTabMuteButtonProps {
  readonly tab: RightPanelSurfaceTab;
  readonly onToggleMute: ((surfaceId: string) => void) | undefined;
}

/**
 * The muted indicator on a preview tab; it doubles as the unmute button
 * where a toggle handler exists.
 */
function SurfaceTabMuteButton(props: SurfaceTabMuteButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={`Unmute ${props.tab.label}`}
            data-testid={`right-sidebar-surface-tab-unmute-${props.tab.kind}`}
            onClick={() => props.onToggleMute?.(props.tab.id)}
            className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm hover:bg-background/70"
          >
            <VolumeOffIcon aria-hidden="true" className="size-3" />
          </button>
        }
      />
      <TooltipPopup side="bottom">Unmute tab</TooltipPopup>
    </Tooltip>
  );
}

interface SurfaceTabLabelProps {
  readonly tab: RightPanelSurfaceTab;
  readonly renaming: boolean;
  readonly onSelect: (surfaceId: string) => void;
  readonly onStartRename: () => void;
  readonly onCommitRename: (value: string) => void;
}

/**
 * The tab's label in its three states: the rename input, the active tab's
 * double-click-to-rename label, and the inactive tab's plain label.
 */
function SurfaceTabLabel(props: SurfaceTabLabelProps) {
  const { tab } = props;
  if (props.renaming) {
    return (
      <input
        aria-label="Tab name"
        className="w-24 min-w-0 rounded-sm bg-background px-1 text-xs outline-none ring-1 ring-ring"
        defaultValue={tab.label}
        data-testid={`right-sidebar-surface-tab-rename-input-${tab.kind}`}
        ref={(element) => {
          element?.focus();
          element?.select();
        }}
        onBlur={(event) => {
          props.onCommitRename(event.currentTarget.value);
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            event.currentTarget.value = tab.label;
            event.currentTarget.blur();
          }
        }}
      />
    );
  }
  if (tab.active) {
    return (
      <span className="min-w-0 max-w-36 cursor-text truncate" onDoubleClick={props.onStartRename}>
        {tab.label}
      </span>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            disabled={tab.disabled}
            aria-label={`Show ${tab.label} surface`}
            data-testid={`right-sidebar-surface-tab-select-${tab.kind}`}
            onClick={tab.disabled ? undefined : () => props.onSelect(tab.id)}
            onDoubleClick={props.onStartRename}
            className={cn(
              "min-h-11 max-w-24 min-w-0 truncate rounded-lg text-left focus-visible:outline-none sm:min-h-0 sm:rounded-none",
              tab.disabled && "cursor-not-allowed",
            )}
          >
            {tab.label}
          </button>
        }
      />
      <TooltipPopup side="bottom">{tab.description}</TooltipPopup>
    </Tooltip>
  );
}
